import { api } from '../services/api';
import { IKanbanResponse } from './useKanban';

// Tracker de legalização societária (Fase 1). O board é renderizado com os
// componentes genéricos do kanban (`components/kanban/*`); a MOVIMENTAÇÃO passa
// por estes endpoints dedicados pra manter o `status` do processo em sincronia
// com a coluna do card (≠ do estatuto, que move pelo `moveCard` genérico).

// As constantes puras moram em `pages/societario/legalizacao/tipos.ts` para que
// quem só precisa do rótulo não arraste o `services/api` (axios) junto. Ficam
// reexportadas aqui porque é daqui que o resto do app já as importa.
import type { TipoLegalizacao } from '../pages/societario/legalizacao/tipos';

export type { TipoLegalizacao };
export { TIPOS_LEGALIZACAO, TIPO_LEGALIZACAO_LABEL } from '../pages/societario/legalizacao/tipos';

// Espelha `ILegalizacaoRead` do backend (app/schemas/legalizacao.py).
export interface ILegalizacao {
  id: number;
  tipo_legalizacao: TipoLegalizacao | string;
  status: string | null;
  empresa_id: number | null;
  kanban_card_id: number | null;
  contrato_societario_id: number | null;
  junta_sigla: string | null;
  junta_uf: string | null;
  municipio: string | null;
  rota: string | null;
  cadastro_confere: boolean | null;
  gop: string | null;
  gon: string | null;
  nire: string | null;
  cnpj: string | null;
  protocolo_numero: string | null;
  protocolo_data: string | null;
  dare_numero: string | null;
  dare_valor: number | null;
  dare_pago: boolean | null;
  // Estado por etapa: checklist marcado + campos de briefing não-tipados.
  estado_etapas: Record<string, unknown> | null;
  responsavel_id: number | null;
  responsavel_nome: string | null;
  dt_criacao: string | null;
  // Preenchidos pelo "Finalizar" da última etapa: o card sai do quadro (arquivado) e o
  // processo passa a aparecer em "Finalizados". `null` = ainda no quadro.
  finalizado_em?: string | null;
  finalizado_por_id?: number | null;
  finalizado_por_nome?: string | null;
  // Último comentário do card (as observações do processo), para a faixa do card no
  // quadro. Back sem o campo: a faixa mostra a observação antiga (`_obs`).
  ultimo_comentario?: ILegalizacaoComentario | null;
  // Título do card (o nome do processo no quadro).
  titulo?: string | null;
}

/** Comentário do card do kanban — é onde moram as observações do processo. */
export interface ILegalizacaoComentario {
  texto: string;
  autor_nome: string | null;
  dt_criacao: string | null;
}

export interface ILegalizacaoCreate {
  tipo_legalizacao: TipoLegalizacao;
  // Constituição: razão social PRETENDIDA (a empresa ainda não existe). Alt/baixa:
  // razão social vinda da consulta do CNPJ. Vira o título do card.
  razao_social?: string | null;
  cnpj?: string | null;
  empresa_id?: number | null;
  junta_sigla?: string | null;
  junta_uf?: string | null;
  municipio?: string | null;
  responsavel_id?: number | null;
  // Prazo de entrega acordado (SLA interno), ISO. Grava em `expira_em` do CARD — é de
  // lá que o semáforo é calculado. Depois de criado só muda pelo bloco "Prazo (SLA)" do
  // drawer, que exige motivo e registra histórico; por isso NÃO existe em
  // `ILegalizacaoUpdate`.
  expira_em?: string | null;
}

export interface ILegalizacaoMoveCard {
  coluna_titulo: string;
}

// Espelha `ILegalizacaoUpdate` do backend (PATCH parcial: só os campos enviados mudam;
// `tipo_legalizacao` é imutável e `status` muda por /mover, não aqui).
export interface ILegalizacaoUpdate {
  empresa_id?: number | null;
  contrato_societario_id?: number | null;
  junta_sigla?: string | null;
  junta_uf?: string | null;
  municipio?: string | null;
  rota?: string | null;
  cadastro_confere?: boolean | null;
  gop?: string | null;
  gon?: string | null;
  nire?: string | null;
  cnpj?: string | null;
  protocolo_numero?: string | null;
  protocolo_data?: string | null;
  dare_numero?: string | null;
  dare_valor?: number | null;
  dare_pago?: boolean | null;
  // Estado por etapa (checklist + briefing) — o PATCH substitui o dict inteiro.
  estado_etapas?: Record<string, unknown> | null;
  responsavel_id?: number | null;
  // Só para a aba Atividade (não é gravado): o back decide o que mudou; daqui vão os
  // nomes da tela e as chaves que o sistema carimbou sozinho neste salvamento.
  atividade?: { rotulos: Record<string, string>; automaticos: string[] };
}

export const useLegalizacao = () => {
  const listarLegalizacoes = async (
    tipo?: TipoLegalizacao,
  ): Promise<IKanbanResponse<ILegalizacao[]>> => {
    const params: Record<string, string> = {};
    if (tipo) params.tipo = tipo;
    const res = await api.get<IKanbanResponse<ILegalizacao[]>>('/api/v1/societario/legalizacoes', {
      params,
    });
    return res.data;
  };

  const obterLegalizacao = async (id: number): Promise<IKanbanResponse<ILegalizacao>> => {
    const res = await api.get<IKanbanResponse<ILegalizacao>>(
      `/api/v1/societario/legalizacoes/${id}`,
    );
    return res.data;
  };

  const criarLegalizacao = async (
    data: ILegalizacaoCreate,
  ): Promise<IKanbanResponse<ILegalizacao>> => {
    const res = await api.post<IKanbanResponse<ILegalizacao>>(
      '/api/v1/societario/legalizacoes',
      data,
    );
    return res.data;
  };

  const moverLegalizacao = async (
    id: number,
    data: ILegalizacaoMoveCard,
  ): Promise<IKanbanResponse<ILegalizacao>> => {
    const res = await api.post<IKanbanResponse<ILegalizacao>>(
      `/api/v1/societario/legalizacoes/${id}/mover`,
      data,
    );
    return res.data;
  };

  const atualizarLegalizacao = async (
    id: number,
    patch: ILegalizacaoUpdate,
  ): Promise<IKanbanResponse<ILegalizacao>> => {
    const res = await api.patch<IKanbanResponse<ILegalizacao>>(
      `/api/v1/societario/legalizacoes/${id}`,
      patch,
    );
    return res.data;
  };

  const finalizarLegalizacao = async (id: number): Promise<IKanbanResponse<ILegalizacao>> => {
    const res = await api.post<IKanbanResponse<ILegalizacao>>(
      `/api/v1/societario/legalizacoes/${id}/finalizar`,
    );
    return res.data;
  };

  const reabrirLegalizacao = async (id: number): Promise<IKanbanResponse<ILegalizacao>> => {
    const res = await api.post<IKanbanResponse<ILegalizacao>>(
      `/api/v1/societario/legalizacoes/${id}/reabrir`,
    );
    return res.data;
  };

  return {
    listarLegalizacoes,
    obterLegalizacao,
    criarLegalizacao,
    moverLegalizacao,
    atualizarLegalizacao,
    finalizarLegalizacao,
    reabrirLegalizacao,
  };
};
